import { Suspense, useCallback, useState, type ReactNode } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { t } from '../../i18n';
import { useAuth } from '../../stores/auth';
import { Loading } from '../ui';
import { DocViewer } from '../DocViewer';
import { PasswordWarning } from '../PasswordWarning';
import { TaskDialog } from '../tasks';
import { CommandPalette, useCommandHotkey } from './CommandPalette';
import { canSee, type Section } from './nav';
import { landingFor, myWorkspaceKeys } from './workspaces';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { ReportWidget } from '../ReportProblem';
import { HelpCards } from '../HelpCards';

export function AppLayout() {
  const status = useAuth((s) => s.status);
  const location = useLocation();
  const [palette, setPalette] = useState(false);
  const openPalette = useCallback(() => setPalette(true), []);
  useCommandHotkey(openPalette);
  if (status === 'unknown' || status === 'loading') return <Loading className="p-10" />;
  if (status !== 'authenticated') return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-canvas">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 bg-bg border border-fg rounded-lg px-3 py-2">
        {t('app.skip')}
      </a>
      <Sidebar />
      <main id="main" className="flex-1 min-w-0 px-4 sm:px-6 md:px-7 pt-5 pb-16">
        <div className="mx-auto max-w-[1440px]">
          <TopBar onSearch={openPalette} />
          <PasswordWarning />
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        </div>
      </main>
      <CommandPalette open={palette} onClose={() => setPalette(false)} />
      <DocViewer />
      <TaskDialog />
      <ReportWidget />
      <HelpCards />
    </div>
  );
}

/**
 * Розділ, якого роль не бачить: пояснюємо це на місці (а не мовчки перекидаємо на головну),
 * щоб посилання зі сповіщення чи пошуку «не працювало» видимо, з посиланням на першу доступну сторінку.
 */
export function Gate({ section, children }: { section: Section; children: ReactNode }) {
  const me = useAuth((s) => s.me);
  // явний допуск до простору (me.workspaces) відкриває і його розділи
  if (canSee(me?.role, section, myWorkspaceKeys(me))) return <>{children}</>;
  return <NoAccess section={section} />;
}

export function NoAccess(_props: { section?: Section }) {
  const me = useAuth((s) => s.me);
  const location = useLocation();
  // «/» веде на головну поточного робочого простору (App.tsx → landingFor)
  const home = me && landingFor(me) !== location.pathname ? { to: '/' } : undefined;
  return (
    <div role="alert" className="max-w-lg mx-auto py-16 flex flex-col items-center gap-3 text-center">
      <h1 className="m-0 text-xl font-bold">{t('nav.noAccessTitle')}</h1>
      <p className="m-0 text-base text-lab">{t('nav.noAccessText')}</p>
      {home && (
        <Link to={home.to} className="btn btn-solid mt-2">
          {t('nav.noAccessHome')}
        </Link>
      )}
    </div>
  );
}
